/* pd-options.js — Podiums Direct options engine, storefront runtime.
 *
 * PDP: intercepts add-to-cart on any form containing [data-pd-options],
 * adds the parent + selected add-on charge variants in ONE /cart/add.js call,
 * then goes to /cart. Selections ride as line-item properties on the parent
 * (including "No ..." declines — same shape the intake pipeline already parses).
 * Add-on lines carry _pd_key (group id) + _pd_for (option label). The parent
 * also carries _pd_addons — JSON [[label, variant_id, once]] of every charge
 * line it was added with — so reconciliation can REBUILD a charge line the
 * shopper deleted, not just clean up orphans.
 *
 * Every page load reconciles the cart both ways: orphaned add-ons removed,
 * quantities synced, deleted charge lines re-added (and if a re-add fails,
 * the parent's option property is stripped so an order can never arrive
 * configured-but-unpaid). The cart templates hide remove/qty controls on
 * add-on lines (see theme_push.py pd-addon-lock); this is the safety net
 * behind that, and intake's CHARGE-INTEGRITY flag backstops both.
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var RELOAD_KEY = 'pd_reconcile_reloads';

  // same format as the Liquid money filter ($1,250.00) so the running total
  // matches the option labels (audit P3-5)
  var money = function (cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  function isCartPage() {
    var p = window.location.pathname.replace(/\/+$/, '');
    var c = (ROOT + 'cart').replace(/\/{2,}/g, '/').replace(/\/+$/, '');
    return p === c;
  }

  /* ---------- PDP widget ---------- */

  function initWidget(widget) {
    if (widget.hasAttribute('data-pd-init')) return;
    widget.setAttribute('data-pd-init', '');
    var form = widget.closest('form');
    if (!form) return;
    var selects = Array.prototype.slice.call(widget.querySelectorAll('[data-pd-option]'));

    // Impulse's qty input joins the form via the form="" attribute, so it is
    // not a DOM descendant — form.elements sees it, querySelector cannot.
    function qtyEl() {
      var el = (form.elements && form.elements['quantity']) || null;
      if (!el) el = form.querySelector('[name="quantity"]');
      return el;
    }
    function getQty() {
      var el = qtyEl();
      return el ? Math.max(1, parseInt(el.value, 10) || 1) : 1;
    }

    var errEl = widget.querySelector('[data-pd-error]');
    function showError(msg) {
      if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
      else alert(msg);
    }
    function clearError() {
      if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
    }

    /* ---------- headline price + breakdown (per Sam 9/10/26) ----------
     * Impulse prints the bare variant price in [data-product-price] and
     * rewrites it (innerHTML) on every variantPriceChange, so the options
     * total used to sit alone at the bottom of the widget while the big
     * price never moved — the opposite of what the native variant picker
     * does. Now the headline shows the CONFIGURED unit price: variant price
     * + per-unit option charges. Once-per-order charges stay OUT of the unit
     * price (they would double at qty 2) and appear on the breakdown, which
     * also carries the order total whenever qty > 1 or a one-time charge is
     * selected. A compare-at price, when one exists, is lifted by the same
     * per-unit option total so the sale math stays true (per Sam). Base
     * prices come from the section's [data-variant-json] keyed by the master
     * select — never from the span text, which is what gets overwritten.
     * Re-applied AFTER the theme's own handler (setTimeout 0 on the theme's
     * events) and backstopped by a MutationObserver on the price spans, so
     * no theme path can leave the bare price showing. Products whose price
     * block or variant JSON can't be found fall back to the old
     * "Total Cost of Options" line inside the widget. */
    var section = widget.closest('[data-section-type="product"]') || widget.closest('.product-section');
    var priceEl = section ? section.querySelector('[data-product-price]') : null;
    var compareEl = section ? section.querySelector('[data-compare-price]') : null;
    var saveEl = section ? section.querySelector('[data-save-price]') : null;
    var totalLine = widget.querySelector('[data-pd-total]');
    var variantsById = null;
    (function () {
      var vj = section ? section.querySelector('[data-variant-json]') : null;
      if (!vj) return;
      try {
        var arr = JSON.parse(vj.value || vj.textContent);
        variantsById = {};
        arr.forEach(function (v) { variantsById[String(v.id)] = v; });
      } catch (e) { variantsById = null; }
    })();
    var headline = !!(priceEl && totalLine && variantsById);
    function currentVariant() {
      if (!variantsById) return null;
      var master = form.querySelector('[data-product-select]') || form.querySelector('[name="id"]');
      return master ? (variantsById[String(master.value)] || null) : null;
    }
    // the theme's own formatter when present, so the headline matches the
    // shop's money format exactly; money() is the same shape as the fallback
    function fmt(cents) {
      try {
        if (window.theme && theme.Currency && theme.Currency.formatMoney && theme.settings && theme.settings.moneyFormat) {
          return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
        }
      } catch (e) {}
      return money(cents);
    }
    function optionTotals() {
      var qty = getQty(), per = 0, once = 0;
      selects.forEach(function (s) {
        var o = s.selectedOptions[0];
        if (!o) return;
        var p = parseInt(o.getAttribute('data-pd-price') || '0', 10) || 0;
        if (o.hasAttribute('data-pd-once')) once += p; else per += p;
      });
      return { qty: qty, per: per, once: once, total: per * qty + once };
    }
    if (headline) {
      // the breakdown moves up under the headline price (after the unit-price
      // wrapper, before the delivery block) and drops the widget's bold style
      var anchor = section.querySelector('[data-unit-price-wrapper]') || priceEl;
      anchor.parentNode.insertBefore(totalLine, anchor.nextSibling);
      totalLine.classList.add('pd-price-breakdown');
    }
    var lastHeadline = null, lastCompare = null;
    function applyHeadline(t) {
      if (!headline) return;
      var v = currentVariant();
      if (!v) return;
      t = t || optionTotals();
      var text = fmt(v.price + t.per);
      if (priceEl.textContent.trim() !== text) priceEl.textContent = text;
      lastHeadline = text;
      if (compareEl && v.compare_at_price && v.compare_at_price > v.price) {
        var ctext = fmt(v.compare_at_price + t.per);
        if (compareEl.textContent.trim() !== ctext) compareEl.textContent = ctext;
        lastCompare = ctext;
        // dollar savings are unchanged when both sides move; percent shifts
        if (saveEl && t.per && window.theme && theme.settings && theme.settings.saveType === 'percent'
            && theme.strings && theme.strings.savePrice) {
          var pct = Math.round((v.compare_at_price - v.price) * 100 / (v.compare_at_price + t.per)) + '%';
          saveEl.innerHTML = theme.strings.savePrice.replace('[saved_amount]', pct);
        }
      }
    }

    function updateTotal() {
      var t = optionTotals();
      if (totalLine) {
        var amt = totalLine.querySelector('[data-pd-total-amount]') || totalLine;
        var v = headline ? currentVariant() : null;
        if (t.per === 0 && t.once === 0) {
          totalLine.hidden = true;
        } else if (v) {
          var parts = [fmt(v.price) + ' base'];
          if (t.per) parts.push('+ ' + fmt(t.per) + ' options');
          if (t.once) parts.push('+ ' + fmt(t.once) + ' one-time');
          var s = parts.join(' ');
          if (t.qty > 1 || t.once) {
            s += ' · Total' + (t.qty > 1 ? ' for ' + t.qty : '') + ': ' + fmt(v.price * t.qty + t.total);
          }
          amt.textContent = s;
          totalLine.hidden = false;
        } else {
          amt.textContent = 'Total Cost of Options: ' + fmt(t.total);
          totalLine.hidden = false;
        }
      }
      applyHeadline(t);
    }
    if (headline) {
      var lateTotal = function () { setTimeout(updateTotal, 0); };
      form.addEventListener('change', lateTotal);
      document.addEventListener('variant:change', lateTotal);
      section.addEventListener('variantChange', lateTotal);
      section.addEventListener('variantPriceChange', lateTotal);
      if (window.MutationObserver) {
        var mo = new MutationObserver(function () {
          if (lastHeadline !== null && priceEl.textContent.trim() !== lastHeadline) applyHeadline();
          else if (compareEl && lastCompare !== null && compareEl.textContent.trim() !== lastCompare) applyHeadline();
        });
        var moOpts = { childList: true, characterData: true, subtree: true };
        mo.observe(priceEl, moOpts);
        if (compareEl) mo.observe(compareEl, moOpts);
      }
    }
    /* image preview — two config shapes on pdo.layer_config:
     *
     * v1 (legacy layers): {base, layers: [{option, value, src} |
     *   {option, value_map: {label: src}}], always_show?} — transparent PNGs
     *   stacked in config order; a layer shows when its option's selection
     *   matches.
     *
     * v2 (combo): {version: 2, combos: {"tok|tok|…": "file.jpg"}, watch: [
     *   {key, source: "variant", variant_map: {variantId: token}, default} |
     *   {key, source: "option", label, map?: {value: token}, any?: token,
     *    default}], base_url?, aspect?, alt?} — ONE pre-rendered image per
     *   full combination (built from the vendor's layered PSDs, see
     *   business/options_engine/scripts/layer_previews.py). Tokens resolve
     *   in watch order and join with "|" to key into combos. source
     *   "variant" tracks the NATIVE variant picker via the form's master
     *   select ([data-product-select] / [name=id]) — Impulse re-points it
     *   on every variant change and fires document 'variant:change'. For an
     *   option: no selection -> default; else map[value], else `any` (the
     *   single-value add-on toggle case), else the raw value. */
    var previewWrap = widget.querySelector('[data-pd-preview]');
    var previewStage = previewWrap ? previewWrap.querySelector('[data-pd-preview-stage]') : null;
    var layerCfg = null;
    if (previewWrap && previewStage) {
      var cfgEl = previewWrap.querySelector('[data-pd-layer-config]');
      try { layerCfg = cfgEl ? JSON.parse(cfgEl.textContent) : null; }
      catch (e) { layerCfg = null; }
    }
    var comboMode = !!(layerCfg && layerCfg.version === 2 && layerCfg.combos);
    var comboImg = null;
    if (comboMode && layerCfg.aspect) previewStage.style.aspectRatio = layerCfg.aspect;

    function pdOptionValue(label) {
      for (var i = 0; i < selects.length; i++) {
        if (selects[i].getAttribute('data-pd-label') === label) {
          var o = selects[i].selectedOptions[0];
          return o ? o.value : '';
        }
      }
      return '';
    }
    function comboSrc() {
      var toks = [];
      var watch = layerCfg.watch || [];
      for (var i = 0; i < watch.length; i++) {
        var w = watch[i], tok;
        if (w.source === 'variant') {
          var master = form.querySelector('[data-product-select]') || form.querySelector('[name="id"]');
          var vid = master ? String(master.value) : '';
          tok = (w.variant_map && w.variant_map[vid] !== undefined) ? w.variant_map[vid] : w.default;
        } else {
          var val = pdOptionValue(w.label);
          if (val === '') tok = w.default;
          else if (w.map && w.map[val] !== undefined) tok = w.map[val];
          else if (w.any !== undefined) tok = w.any;
          else tok = val;
        }
        if (tok === undefined || tok === null) return null;
        toks.push(String(tok));
      }
      var file = layerCfg.combos[toks.join('|')];
      return file ? (layerCfg.base_url || '') + file : null;
    }
    function updatePreview() {
      if (!layerCfg || !previewWrap || !previewStage) return;
      if (comboMode) {
        var src = comboSrc();
        if (!src) {
          // unknown combo: keep showing the last good image rather than blink
          if (!comboImg) previewWrap.hidden = true;
          return;
        }
        previewWrap.hidden = false;
        if (!comboImg) {
          comboImg = document.createElement('img');
          comboImg.alt = layerCfg.alt || 'Preview of the selected options';
          comboImg.decoding = 'async';
          previewStage.innerHTML = '';
          previewStage.appendChild(comboImg);
          comboImg.src = src;
        } else if (comboImg.getAttribute('src') !== src) {
          // swap only once the new combo has loaded — no blank flash
          var pre = new Image();
          pre.onload = function () { comboImg.src = src; };
          pre.src = src;
        }
        return;
      }
      var current = {};
      selects.forEach(function (s) {
        var o = s.selectedOptions[0];
        if (o && o.value) current[s.getAttribute('data-pd-label')] = o.value;
      });
      var srcs = [];
      if (layerCfg.base) srcs.push(layerCfg.base);
      (layerCfg.layers || []).forEach(function (l) {
        var sel = current[l.option];
        if (sel === undefined) return;
        if (l.value_map && l.value_map[sel]) srcs.push(l.value_map[sel]);
        else if (l.value && l.value === sel && l.src) srcs.push(l.src);
      });
      var haveOverlay = srcs.length > (layerCfg.base ? 1 : 0);
      previewWrap.hidden = !haveOverlay && !layerCfg.always_show;
      previewStage.innerHTML = '';
      srcs.forEach(function (src) {
        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        previewStage.appendChild(img);
      });
    }

    var refreshCards = null;      // set by initCards (pdo.card_ui products)
    var onOptionInteract = null;  // set by initGalleryPreview: pull the build view up

    function onChange() {
      updateTotal();
      updatePreview();
      if (refreshCards) refreshCards();
      if (onOptionInteract) onOptionInteract();
    }
    selects.forEach(function (s) { s.addEventListener('change', onChange); });
    if (comboMode) {
      // the stain lives on the NATIVE variant picker, not a pd select. The
      // theme's own change handler re-points the master select, so re-read
      // AFTER it ran (setTimeout 0); 'variant:change' covers programmatic
      // switches (history/URL variant deep-links).
      var lateUpdate = function () {
        setTimeout(function () {
          updatePreview();
          if (refreshCards) refreshCards();
          if (onOptionInteract) onOptionInteract();
        }, 0);
      };
      form.addEventListener('change', lateUpdate);
      document.addEventListener('variant:change', lateUpdate);
    }
    var q = qtyEl();
    if (q && q.addEventListener) {
      q.addEventListener('change', onChange);
      q.addEventListener('input', onChange);
    }
    updateTotal();
    updatePreview();

    /* ---------- card UI + gallery preview (pdo.card_ui products only) ------
     * Presentation layer ONLY: cards drive the existing hidden selects and
     * dispatch 'change', so totals, preview, submit interception and cart
     * reconciliation run the exact same code paths as the dropdown UI.
     * Config (metafield pdo.card_ui, json):
     *   {groups: [{title, note, options: [label, …]}],
     *    options: {label: {kind: "toggle"|"stepper", title, desc, unit,
     *              thumb: {src} | {combo: {watchKey: token}}, crop: {w,x,y}}},
     *    gallery_preview: true, default_no: true}
     * A label whose select shape doesn't match its kind (e.g. a toggle with
     * two values) is left as a visible dropdown — never silently dropped. */
    var cardCfg = null;
    var cardCfgEl = widget.querySelector('[data-pd-card-config]');
    if (cardCfgEl) {
      try { cardCfg = JSON.parse(cardCfgEl.textContent); } catch (e) { cardCfg = null; }
    }

    function fireChange(el) {
      var ev;
      try { ev = new Event('change', { bubbles: true }); }
      catch (e) { ev = document.createEvent('HTMLEvents'); ev.initEvent('change', true, false); }
      el.dispatchEvent(ev);
    }

    // like comboSrc(), but the option keys come from `overrides` + watch
    // defaults instead of the current selections — each card's thumbnail
    // shows ONLY its own change, on the currently selected stain.
    function comboSrcWith(overrides) {
      if (!comboMode) return null;
      var toks = [];
      var watch = layerCfg.watch || [];
      for (var i = 0; i < watch.length; i++) {
        var w = watch[i], tok;
        if (overrides && overrides[w.key] !== undefined) tok = overrides[w.key];
        else if (w.source === 'variant') {
          var master = form.querySelector('[data-product-select]') || form.querySelector('[name="id"]');
          var vid = master ? String(master.value) : '';
          tok = (w.variant_map && w.variant_map[vid] !== undefined) ? w.variant_map[vid] : w.default;
        } else tok = w.default;
        if (tok === undefined || tok === null) return null;
        toks.push(String(tok));
      }
      var file = layerCfg.combos[toks.join('|')];
      return file ? (layerCfg.base_url || '') + file : null;
    }

    function initCards() {
      var byLabel = {};
      selects.forEach(function (s) { byLabel[s.getAttribute('data-pd-label')] = s; });
      var refreshers = [];
      var claimed = {};

      function optionEls(sel) {   // real values (skip the placeholder)
        return Array.prototype.slice.call(sel.options, 1);
      }
      function priceOf(opt) {
        return parseInt(opt.getAttribute('data-pd-price') || '0', 10) || 0;
      }
      function cardPic(cfg) {
        var pic = document.createElement('span');
        pic.className = 'pd-card__pic';
        var img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        if (cfg.crop) {   // {w: 230, x: -60, y: -10} — % of the pic box
          img.style.width = (cfg.crop.w || 100) + '%';
          img.style.height = 'auto';
          img.style.left = (cfg.crop.x || 0) + '%';
          img.style.top = (cfg.crop.y || 0) + '%';
          img.style.objectFit = 'unset';
        }
        if (cfg.thumb && cfg.thumb.src) img.src = cfg.thumb.src;
        else if (cfg.thumb && cfg.thumb.combo) {
          refreshers.push(function () {
            var src = comboSrcWith(cfg.thumb.combo);
            if (src && img.getAttribute('src') !== src) img.src = src;
          });
        }
        pic.appendChild(img);
        return pic;
      }
      function cardShell(sel, cfg) {
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'pd-card';
        card.appendChild(cardPic(cfg));
        var body = document.createElement('span');
        var t = document.createElement('span');
        t.className = 'pd-card__title';
        t.textContent = cfg.title || sel.getAttribute('data-pd-label');
        body.appendChild(t);
        if (cfg.desc) {
          var dsc = document.createElement('span');
          dsc.className = 'pd-card__desc';
          dsc.textContent = cfg.desc;
          body.appendChild(dsc);
        }
        card.appendChild(body);
        var right = document.createElement('span');
        right.className = 'pd-card__right';
        var price = document.createElement('span');
        price.className = 'pd-card__price';
        var tick = document.createElement('span');
        tick.className = 'pd-card__tick';
        tick.textContent = '✓';
        right.appendChild(price);
        right.appendChild(tick);
        card.appendChild(right);
        return { card: card, body: body, price: price };
      }

      function buildToggle(sel, cfg) {
        var opts = optionEls(sel);
        if (opts.length !== 1) return null;
        var opt = opts[0];
        var ui = cardShell(sel, cfg);
        var p = priceOf(opt);
        if (opt.disabled) {
          ui.card.disabled = true;
          ui.price.textContent = 'Sold out';
        } else if (p > 0) {
          ui.price.textContent = '+ ' + money(p);
        } else {
          ui.price.textContent = 'Included';
          ui.price.className += ' pd-card__price--free';
        }
        ui.card.addEventListener('click', function () {
          if (opt.disabled) return;
          sel.value = (sel.value === opt.value) ? '' : opt.value;
          fireChange(sel);
        });
        refreshers.push(function () {
          ui.card.classList.toggle('pd-card--on', sel.value === opt.value);
          ui.card.setAttribute('aria-pressed', sel.value === opt.value ? 'true' : 'false');
        });
        return ui.card;
      }

      function buildStepper(sel, cfg) {
        var opts = optionEls(sel);
        if (opts.length < 2) return null;
        var ui = cardShell(sel, cfg);
        var unit = cfg.unit || 'each';
        var step = document.createElement('span');
        step.className = 'pd-card__step';
        var minus = document.createElement('button');
        minus.type = 'button';
        minus.textContent = '−';
        minus.setAttribute('aria-label', 'Fewer');
        var n = document.createElement('span');
        n.className = 'pd-card__step-n';
        var plus = document.createElement('button');
        plus.type = 'button';
        plus.textContent = '+';
        plus.setAttribute('aria-label', 'More');
        var unitEl = document.createElement('span');
        unitEl.className = 'pd-card__step-unit';
        step.appendChild(minus);
        step.appendChild(n);
        step.appendChild(plus);
        step.appendChild(unitEl);
        ui.body.appendChild(step);

        function count() {
          var i = sel.selectedIndex;   // 0 = placeholder = 0 of them
          return i <= 0 ? 0 : i - 1;
        }
        function setCount(c) {
          if (c < 0) c = 0;
          if (c > opts.length - 1) c = opts.length - 1;
          if (opts[c].disabled) return;
          sel.selectedIndex = c + 1;
          fireChange(sel);
        }
        minus.addEventListener('click', function (ev) { ev.stopPropagation(); setCount(count() - 1); });
        plus.addEventListener('click', function (ev) { ev.stopPropagation(); setCount(count() + 1); });
        ui.card.addEventListener('click', function () { setCount(count() > 0 ? 0 : 1); });
        refreshers.push(function () {
          var c = count();
          n.textContent = String(c);
          unitEl.textContent = unit + (c === 1 ? '' : 's');
          ui.card.classList.toggle('pd-card--on', c > 0);
          var cur = c > 0 ? priceOf(opts[c]) : 0;
          var per = priceOf(opts[0]) === 0 && opts.length > 1 ? priceOf(opts[1]) : priceOf(opts[0]);
          ui.price.textContent = c > 0 ? '+ ' + money(cur) : '+ ' + money(per) + ' / ' + unit;
        });
        return ui.card;
      }

      var container = document.createElement('div');
      container.className = 'pd-cards';
      (cardCfg.groups || []).forEach(function (g) {
        var made = [];
        (g.options || []).forEach(function (label) {
          var sel = byLabel[label];
          var cfg = (cardCfg.options || {})[label];
          if (!sel || !cfg || claimed[label]) return;
          var card = cfg.kind === 'stepper' ? buildStepper(sel, cfg) : buildToggle(sel, cfg);
          if (!card) return;
          claimed[label] = card;
          var field = sel.closest('.pd-options__field');
          if (field) field.classList.add('pd-options__field--carded');
          made.push(card);
        });
        if (!made.length) return;
        var groupEl = document.createElement('div');
        groupEl.className = 'pd-cards__group';
        if (g.title) {
          var titleEl = document.createElement('p');
          titleEl.className = 'pd-cards__group-title';
          titleEl.textContent = g.title;
          if (g.note) {
            var noteEl = document.createElement('small');
            noteEl.textContent = ' — ' + g.note;
            titleEl.appendChild(noteEl);
          }
          groupEl.appendChild(titleEl);
        }
        var list = document.createElement('div');
        list.className = 'pd-cards__list';
        made.forEach(function (c) { list.appendChild(c); });
        groupEl.appendChild(list);
        container.appendChild(groupEl);
      });
      if (!container.children.length) return;

      // "default no": a REQUIRED carded select that still shows its placeholder
      // is preselected to its first free value (e.g. "No Color Fill"), so the
      // hidden select can never invisibly block reportValidity(). One that has
      // no free value stays a visible dropdown instead.
      if (cardCfg.default_no !== false) {
        selects.forEach(function (s) {
          if (!claimed[s.getAttribute('data-pd-label')]) return;
          if (!s.hasAttribute('required') || s.value !== '') return;
          var os = optionEls(s);
          for (var i = 0; i < os.length; i++) {
            if (priceOf(os[i]) === 0 && !os[i].disabled) { s.selectedIndex = i + 1; break; }
          }
          if (s.value === '') {
            var field = s.closest('.pd-options__field');
            if (field) field.classList.remove('pd-options__field--carded');
            // and drop its card too — a required select with no free value
            // must be answered in ONE place (NC8/NC8WC Tinted Glass 9/15/26
            // rendered card + dropdown side by side)
            var stale = claimed[s.getAttribute('data-pd-label')];
            if (stale && stale.parentNode) {
              var lst = stale.parentNode;
              lst.removeChild(stale);
              if (!lst.children.length && lst.parentNode) lst.parentNode.parentNode.removeChild(lst.parentNode);
            }
          }
        });
        if (!container.children.length) return;
      }

      widget.classList.add('pd-options--cards');
      widget.insertBefore(container, widget.firstChild);
      refreshCards = function () { refreshers.forEach(function (f) { f(); }); };
      refreshCards();
      updateTotal();
      updatePreview();
    }

    // Relocate the combo preview stage into the theme's gallery column with a
    // "Your build / Photos" switch. Falls back to the in-widget preview when
    // the gallery container isn't found (quick view, unexpected markup).
    function initGalleryPreview() {
      if (!cardCfg.gallery_preview || !comboMode || !previewWrap) return;
      var section = widget.closest('.product-section');
      var mediaGroup = section ? section.querySelector('[data-product-single-media-group]') : null;
      if (!mediaGroup || mediaGroup.querySelector('.pd-preview-slot')) return;

      var slot = document.createElement('div');
      slot.className = 'pd-preview-slot';
      var toggle = document.createElement('div');
      toggle.className = 'pd-gallery-toggle';
      toggle.setAttribute('role', 'group');
      toggle.setAttribute('aria-label', 'Product image view');
      var bBuild = document.createElement('button');
      bBuild.type = 'button';
      bBuild.textContent = cardCfg.build_label || 'Your build';
      var bPhotos = document.createElement('button');
      bPhotos.type = 'button';
      bPhotos.textContent = cardCfg.photos_label || 'Photos';
      toggle.appendChild(bBuild);
      toggle.appendChild(bPhotos);
      slot.appendChild(previewWrap);
      var hint = document.createElement('p');
      hint.className = 'pd-build-hint';
      hint.textContent = cardCfg.hint || 'This preview updates as you choose your options.';
      slot.appendChild(hint);
      mediaGroup.insertBefore(toggle, mediaGroup.firstChild);
      mediaGroup.insertBefore(slot, toggle.nextSibling);

      function setMode(build) {
        mediaGroup.classList.toggle('pd-build-active', build);
        bBuild.setAttribute('aria-pressed', build ? 'true' : 'false');
        bPhotos.setAttribute('aria-pressed', build ? 'false' : 'true');
        if (!build) {
          // flickity needs a resize kick after being unhidden
          try { window.dispatchEvent(new Event('resize')); } catch (e) {}
        }
      }
      bBuild.addEventListener('click', function () { setMode(true); });
      bPhotos.addEventListener('click', function () { setMode(false); });
      var thumbs = section.querySelector('[data-product-thumbs]');
      if (thumbs) thumbs.addEventListener('click', function () { setMode(false); });
      onOptionInteract = function () {
        if (!mediaGroup.classList.contains('pd-build-active')) setMode(true);
      };
      setMode(true);
    }

    // Move the collapsible detail rows (description, specs, shipping,
    // warranty, ask-a-question) out of the buy column into ONE full-width
    // section under the two-column grid (per Sam 8/16/26 — the mockup layout).
    // The rows are self-contained Impulse collapsibles, so relocating the
    // nodes keeps their behavior; the first row (description) opens by
    // default. No-ops when the theme markup isn't the expected shape.
    function initDetailsBelow() {
      if (!cardCfg.details_below) return;
      var section = widget.closest('.product-section');
      if (!section || section.querySelector('.pd-details-below')) return;
      var blocksHolder = section.querySelector('[data-product-blocks]');
      var grid = section.querySelector('.page-width > .grid');
      if (!blocksHolder || !grid) return;
      var tabs = blocksHolder.querySelectorAll('.product-block--tab');
      if (!tabs.length) return;
      var holder = document.createElement('div');
      holder.className = 'pd-details-below';
      Array.prototype.forEach.call(tabs, function (t) { holder.appendChild(t); });
      grid.parentNode.insertBefore(holder, grid.nextSibling);
      // two-column flow may only break at a heading: wrap each heading + its
      // following content in an unbreakable block, so a column always begins
      // on a heading. Tabs without headings are left to flow freely.
      Array.prototype.forEach.call(holder.querySelectorAll('.collapsible-content__inner'), function (inner) {
        if (!inner.querySelector('h1,h2,h3,h4')) return;
        var groups = [];
        var cur = null;
        while (inner.firstChild) {
          var n = inner.firstChild;
          if (n.nodeType === 1 && /^H[1-4]$/.test(n.tagName)) cur = null;
          if (!cur) {
            cur = document.createElement('div');
            cur.className = 'pd-colsec';
            groups.push(cur);
          }
          cur.appendChild(n);
        }
        groups.forEach(function (g) { inner.appendChild(g); });
      });
      // open the first row (the description) the way force_open renders it
      var trigger = holder.querySelector('.collapsible-trigger');
      var content = holder.querySelector('.collapsible-content');
      if (trigger && content && !trigger.classList.contains('is-open')) {
        trigger.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        content.classList.add('is-open');
        content.style.height = 'auto';
      }
    }

    if (cardCfg && cardCfg.options) {
      initCards();
      initGalleryPreview();
      initDetailsBelow();
    }

    // JS fallback for browsers without :has()
    var pays = form.querySelectorAll('.shopify-payment-button, .additional-checkout-buttons');
    Array.prototype.forEach.call(pays, function (el) { el.style.display = 'none'; });

    var submitting = false;
    // Back button after a successful add: the bfcache restores the page with
    // submitting=true and the button disabled, so nothing works until a reload
    // (found 9/15/26 via the Buy now flow). Reset on pageshow — only what WE
    // disabled, so a sold-out button stays disabled.
    window.addEventListener('pageshow', function () {
      if (!submitting) return;
      submitting = false;
      var b = form.querySelector('[data-add-to-cart]');
      if (b) { b.removeAttribute('disabled'); b.removeAttribute('aria-busy'); }
    });
    form.addEventListener('submit', function (ev) {
      if (!form.reportValidity()) { ev.preventDefault(); ev.stopImmediatePropagation(); return; }
      ev.preventDefault();
      ev.stopImmediatePropagation();
      // Enter-key submits aren't blocked by a disabled button — one add only
      if (submitting) return;

      var idInput = form.querySelector('[name="id"]');
      var parentId = idInput ? parseInt(idInput.value, 10) : NaN;
      var qty = getQty();
      if (!parentId) return;

      var key = 'pd-' + parentId + '-' + Date.now().toString(36);
      var props = { _pd_key: key };
      var addons = [];
      var addonIndex = [];   // [label, variant_id, once] per charge line -> _pd_addons
      selects.forEach(function (s) {
        var o = s.selectedOptions[0];
        if (!o || o.value === '') return;
        props[s.getAttribute('data-pd-label')] = o.value;
        var vid = o.getAttribute('data-pd-variant');
        if (vid) {
          // once-per-order add-ons (SC's ONE_TIME_PER_ORDER, e.g. Flat Ship Rate)
          // charge once regardless of parent quantity
          var once = o.hasAttribute('data-pd-once');
          var aprops = { _pd_key: key, _pd_for: s.getAttribute('data-pd-label') };
          if (once) aprops._pd_once = '1';
          addons.push({
            id: parseInt(vid, 10),
            quantity: once ? 1 : qty,
            properties: aprops
          });
          addonIndex.push([s.getAttribute('data-pd-label'), parseInt(vid, 10), once ? 1 : 0]);
        }
      });
      // The parent remembers its own charge lines so reconciliation can rebuild
      // one the shopper deleted (audit P1-2).
      if (addonIndex.length) props._pd_addons = JSON.stringify(addonIndex);

      var items = [{ id: parentId, quantity: qty, properties: props }].concat(addons);
      var btn = form.querySelector('[data-add-to-cart]');
      submitting = true;
      clearError();
      if (btn) { btn.setAttribute('disabled', 'disabled'); btn.setAttribute('aria-busy', 'true'); }
      fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || e.message || 'add failed'); });
        if (form.dataset.pdBuyNow) {
          return r.json().then(function (d) { finishBuyNow(form, d); }, function () { finishBuyNow(form); });
        }
        window.location.href = ROOT + 'cart';
      }).catch(function (e) {
        submitting = false;
        if (btn) { btn.removeAttribute('disabled'); btn.removeAttribute('aria-busy'); }
        showError('Could not add to cart: ' + e.message);
      });
    }, true);
  }

  /* ---------- cart reconciliation ---------- */

  function reconcileCart() {
    fetch(ROOT + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      var parents = {};   // _pd_key -> parent line
      var addons = [];    // add-on lines
      cart.items.forEach(function (it) {
        var p = it.properties || {};
        if (!p._pd_key) return;
        if (p._pd_for) addons.push(it); else parents[p._pd_key] = it;
      });
      var fixes = [];
      addons.forEach(function (a) {
        var parent = parents[a.properties._pd_key];
        var want = a.properties._pd_once ? 1 : (parent && parent.quantity);
        if (!parent) fixes.push({ id: a.key, quantity: 0 });          // orphan
        else if (a.quantity !== want) fixes.push({ id: a.key, quantity: want });
      });
      // Reverse direction (audit P1-2): the parent's _pd_addons records the
      // charge lines it was added with — rebuild any the shopper deleted, so
      // the configured option can't ride free.
      var adds = [];
      Object.keys(parents).forEach(function (key) {
        var parent = parents[key];
        var expected = [];
        try { expected = JSON.parse(parent.properties._pd_addons || '[]'); } catch (e) {}
        expected.forEach(function (x) {   // x = [label, variant_id, once]
          if (!x || !x[1]) return;
          var have = addons.some(function (a) {
            return a.properties._pd_key === key && a.properties._pd_for === x[0];
          });
          if (have) return;
          var aprops = { _pd_key: key, _pd_for: x[0] };
          if (x[2]) aprops._pd_once = '1';
          adds.push({ parent: parent, label: x[0],
                      item: { id: x[1], quantity: x[2] ? 1 : parent.quantity, properties: aprops } });
        });
      });
      if (!fixes.length && !adds.length) {
        try { sessionStorage.removeItem(RELOAD_KEY); } catch (e) {}
        return;
      }
      var failed = false;
      var done = function () {
        if (!isCartPage()) return;
        // reload so the page shows the fixed lines — but never loop: a fix
        // that can't take effect (e.g. add-on inventory clamps below the
        // parent qty) would otherwise re-fix + re-reload forever
        var n = 0;
        try { n = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10); } catch (e) {}
        if (failed || n >= 2) return;
        try { sessionStorage.setItem(RELOAD_KEY, String(n + 1)); } catch (e) {}
        window.location.reload();
      };
      // A charge line that can't be rebuilt (variant sold out/retired) must not
      // leave the order configured-but-unpaid: strip that option's property and
      // its _pd_addons entry from the parent. One strip per parent (batched —
      // each /cart/change.js on a line changes its key), resending the full
      // properties object minus the stripped labels (change.js REPLACES it).
      // A successful strip is a real fix: the next pass finds nothing and the
      // reload counter clears, so this can't loop.
      var stripByKey = {};  // parent line key -> {parent, labels: []}
      var runStrips = function () {
        var keys = Object.keys(stripByKey);
        if (!keys.length) { done(); return; }
        var s = stripByKey[keys[0]];
        delete stripByKey[keys[0]];
        var props = {};
        Object.keys(s.parent.properties || {}).forEach(function (k) {
          if (s.labels.indexOf(k) === -1) props[k] = s.parent.properties[k];
        });
        var kept = [];
        try { kept = JSON.parse(s.parent.properties._pd_addons || '[]'); } catch (e) {}
        props._pd_addons = JSON.stringify(kept.filter(function (x) {
          return x && s.labels.indexOf(x[0]) === -1;
        }));
        fetch(ROOT + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: s.parent.key, properties: props })
        }).then(function (r) {
          if (!r.ok) failed = true;
        }).catch(function () {
          failed = true;
        }).then(runStrips);
      };
      var runAdds = function () {
        var a = adds.shift();
        if (!a) { runStrips(); return; }
        fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [a.item] })
        }).then(function (r) {
          if (!r.ok) {
            var st = stripByKey[a.parent.key] || (stripByKey[a.parent.key] = { parent: a.parent, labels: [] });
            st.labels.push(a.label);
          }
        }).catch(function () {
          failed = true;   // network error: retry next pass, don't strip
        }).then(runAdds);
      };
      var run = function () {
        var f = fixes.shift();
        if (!f) { runAdds(); return; }
        fetch(ROOT + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(f)
        }).then(function (r) {
          if (!r.ok) failed = true;   // a failed fix must not stop the queue
        }).catch(function () {
          failed = true;
        }).then(run);
      };
      run();
    }).catch(function () {});
  }

  /* ---------- Buy now (pd-buy-now v1, 9/15/26 per Leo) ----------
     A product-form button that adds the product and goes straight to
     /checkout. Widget forms go through the engine's own submit handler above
     (form.dataset.pdBuyNow makes its success path call finishBuyNow instead
     of loading /cart), so the add-on charge lines are never skipped; plain
     forms post their own FormData to cart/add.js. A freight product asks the
     loading-dock question inline first (snippets/pd-buy-now.liquid) and the
     answer is saved as the cart attribute "Loading dock" — the same one the
     cart page writes — before the redirect. */
  function dockAnswer(wrap) {
    var r = wrap.querySelector('[data-pd-buy-now-dock] input[type="radio"]:checked');
    return r ? r.value : '';
  }

  /* A shopper who reaches checkout, hits Back and clicks Buy now again should
     not end up with the item twice (Shopify's native button did exactly that).
     finishBuyNow remembers the cart line keys it added plus a fingerprint of the
     form's selections in sessionStorage; the next click checks the cart: same
     fingerprint and the lines are still there -> straight to checkout, no add;
     different selections -> the earlier Buy now lines are removed first, then the
     new configuration is added. A completed order empties the cart, so the memo
     simply stops matching. */
  var BUY_NOW_MEMO = 'pd_buy_now';
  function memoRead() {
    try {
      var m = JSON.parse(sessionStorage.getItem(BUY_NOW_MEMO) || 'null');
      if (m && m.keys && m.keys.length && (Date.now() - (m.ts || 0)) < 2 * 3600 * 1000) return m;
    } catch (e) {}
    return null;
  }
  function memoWrite(fp, added) {
    try {
      var items = added ? (added.items || [added]) : [];
      var keys = items.map(function (i) { return i && i.key; }).filter(Boolean);
      if (keys.length) sessionStorage.setItem(BUY_NOW_MEMO, JSON.stringify({ fp: fp, keys: keys, ts: Date.now() }));
    } catch (e) {}
  }
  function formFingerprint(form) {
    var pairs = [];
    try {
      new FormData(form).forEach(function (v, k) {
        if (k === 'form_type' || k === 'utf8') return;
        pairs.push(k + '=' + (typeof v === 'string' ? v : ''));
      });
    } catch (e) {}
    pairs.sort();
    return form.getAttribute('action') + '|' + pairs.join('&');
  }
  // resolves true when the current configuration is already in the cart from a
  // previous Buy now (skip the add); removes stale Buy now lines otherwise
  function buyNowRepeatCheck(fp) {
    var prev = memoRead();
    if (!prev) return Promise.resolve(false);
    return fetch(ROOT + 'cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var lines = (cart.items || []).filter(function (i) { return prev.keys.indexOf(i.key) >= 0; });
        if (!lines.length) return false;
        if (prev.fp === fp) return true;
        return lines.reduce(function (p, l) {
          return p.then(function () {
            return fetch(ROOT + 'cart/change.js', {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: l.key, quantity: 0 })
            });
          });
        }, Promise.resolve()).then(function () { return false; });
      })
      .catch(function () { return false; });
  }

  function finishBuyNow(form, added) {
    var wrap = form.querySelector('[data-pd-buy-now-wrap]');
    if (added) memoWrite(form.dataset.pdBuyNowFp || '', added);
    var go = function () { window.location.href = ROOT + 'checkout'; };
    var answer = wrap ? dockAnswer(wrap) : '';
    if (!answer) { go(); return; }
    fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { 'Loading dock': answer } })
    }).then(go, go);
  }

  function initBuyNow(wrap) {
    if (wrap.hasAttribute('data-pd-bn-init')) return;
    wrap.setAttribute('data-pd-bn-init', '');
    var form = wrap.closest('form');
    var btn = wrap.querySelector('[data-pd-buy-now]');
    if (!form || !btn) return;
    var dock = wrap.querySelector('[data-pd-buy-now-dock]');
    var dockErr = wrap.querySelector('[data-pd-cart-dock-error]');
    var err = wrap.querySelector('[data-pd-buy-now-error]');
    var busy = false;
    function idle() {
      busy = false;
      delete form.dataset.pdBuyNow;
      btn.removeAttribute('disabled'); btn.removeAttribute('aria-busy');
    }
    // Back button after Buy now reached checkout: the bfcache restores the page
    // with busy=true and the button disabled, so a second click did nothing.
    window.addEventListener('pageshow', function () { if (busy) idle(); });
    function fail(msg) {
      idle();
      if (err) { err.textContent = msg; err.hidden = false; }
    }
    if (dock) {
      dock.addEventListener('change', function () {
        dock.classList.remove('pd-cart-dock--missing');
        if (dockErr) dockErr.hidden = true;
      });
    }
    btn.addEventListener('click', function () {
      if (busy) return;
      if (err) err.hidden = true;
      if (form.reportValidity && !form.reportValidity()) return;
      // pd-dock-optional (9/30/26, per Leo): the dock question never blocks Buy now
      busy = true;
      btn.setAttribute('disabled', 'disabled'); btn.setAttribute('aria-busy', 'true');
      form.dataset.pdBuyNow = '1';
      var fp = formFingerprint(form);
      form.dataset.pdBuyNowFp = fp;
      buyNowRepeatCheck(fp).then(function (alreadyInCart) {
        if (!busy) return;                       // reset by pageshow meanwhile
        if (alreadyInCart) { finishBuyNow(form); return; }
        if (form.querySelector('[data-pd-options]')) {
          // engine form: its capture-phase submit handler adds parent + charge
          // lines, then (seeing pdBuyNow) calls finishBuyNow
          if (form.requestSubmit) form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          // success navigates away; the engine re-enables its add button only on
          // failure, so a re-enabled button while we are busy means the add failed
          var watch = setInterval(function () {
            var add = form.querySelector('[data-add-to-cart]');
            if (!busy) { clearInterval(watch); return; }
            if (add && !add.hasAttribute('disabled')) {
              clearInterval(watch);
              var e = form.querySelector('[data-pd-error]:not([hidden])');
              fail((e && e.textContent) || 'Could not add to cart.');
            }
          }, 300);
          setTimeout(function () { clearInterval(watch); }, 20000);
          return;
        }
        var fd = new FormData(form);
        fd.delete('form_type'); fd.delete('utf8');
        fetch(ROOT + 'cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || e.message || 'add failed'); });
            return r.json().then(function (d) { finishBuyNow(form, d); }, function () { finishBuyNow(form); });
          })
          .catch(function (e) { fail('Could not add to cart: ' + e.message); });
      });
    });
  }

  /* ---------- init (incl. late-rendered forms: quick view, section loads) ---------- */

  function initAll(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pd-options]'), initWidget);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pd-buy-now-wrap]'), initBuyNow);
  }

  function init() {
    initAll(document);
    // every page, not just /cart: a shopper who edits the cart in the ajax
    // drawer can reach checkout without ever loading /cart, so the drawer
    // path would otherwise skip reconciliation entirely (the reload-on-fix
    // behavior stays cart-page-only, inside done())
    reconcileCart();
    // Quick view / theme-editor section loads inject product forms after
    // DOMContentLoaded; without a late init their add-to-cart would post
    // option properties with NO add-on charges.
    document.addEventListener('shopify:section:load', function (ev) {
      initAll(ev.target || document);
    });
    if (window.MutationObserver && document.body) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.hasAttribute && n.hasAttribute('data-pd-options')) initWidget(n);
            else if (n.hasAttribute && n.hasAttribute('data-pd-buy-now-wrap')) initBuyNow(n);
            else if (n.querySelector && n.querySelector('[data-pd-options], [data-pd-buy-now-wrap]')) initAll(n);
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
